/**
 * Override Resolver — pure functions, no I/O.
 * Resolves the effective ClassSchedule for a (classId, date) pair
 * given a list of ScheduleOverride rows and the class's default schedule.
 *
 * Requirements: 1.3, 1.7, 3.2, 3.3, 3.6, 4.2, 4.3, 5.1, 5.2, 5.3, 5.4,
 *               7.1, 7.2, 7.3, 7.4, 7.5, 7.7
 */

import type {
  ScheduleOverride,
  CreateScheduleOverrideRequest,
  UpdateScheduleOverrideRequest,
} from '@timetable/types';
import type { ClassScheduleWithPeriods, OverrideResolutionResult } from '@timetable/types';

// ---------------------------------------------------------------------------
// ISO week helpers
// ---------------------------------------------------------------------------

/**
 * Returns the ISO 8601 week number for a YYYY-MM-DD date string.
 * ISO weeks start on Monday; week 1 contains the first Thursday of the year.
 */
export function getIsoWeekNumber(date: string): number {
  const d = new Date(date + 'T00:00:00Z');
  // Move to nearest Thursday: current date + 4 - current day number (Mon=1)
  const dayNum = d.getUTCDay() || 7; // convert Sun=0 → 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/**
 * Returns which Saturday of the month the given date falls on.
 * E.g. the 1st Saturday of the month → 1, the 3rd → 3, etc.
 * The date must be a Saturday; behaviour is undefined for other days.
 */
export function getSaturdayPositionInMonth(date: string): 1 | 2 | 3 | 4 | 5 {
  const d = new Date(date + 'T00:00:00Z');
  const dayOfMonth = d.getUTCDate();
  // Count how many Saturdays have occurred in this month up to and including this date.
  // Since every 7 days is one Saturday, position = ceil(dayOfMonth / 7).
  const position = Math.ceil(dayOfMonth / 7) as 1 | 2 | 3 | 4 | 5;
  return position;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Returns the uppercase day-of-week name for a YYYY-MM-DD string. */
function getDayOfWeek(date: string): string {
  const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  const d = new Date(date + 'T00:00:00Z');
  return days[d.getUTCDay()];
}

/** Returns true if `date` falls within [start_date, end_date] inclusive. */
function isDateInRange(date: string, start: string, end: string): boolean {
  return date >= start && date <= end;
}

/**
 * Tie-break: among overrides of the same priority, return the one with the
 * most recent `created_at` (DESC). Returns a new sorted array.
 */
function sortByCreatedAtDesc(overrides: ScheduleOverride[]): ScheduleOverride[] {
  return [...overrides].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

// ---------------------------------------------------------------------------
// Core resolver
// ---------------------------------------------------------------------------

/**
 * Resolves the effective schedule for a class on a specific date.
 *
 * Priority order (highest → lowest):
 *  1. holiday
 *  2. day-specific half_day_schedule (day_of_week matches weekday of date)
 *  3. alternate_saturday
 *  4. ALL-day half_day_schedule (day_of_week IS NULL or 'ALL')
 *  5. default class schedule
 *
 * Tie-breaking within the same priority level: most recently created wins.
 *
 * Requirements: 1.3, 3.2, 3.3, 4.2, 4.3, 5.1, 5.2, 5.3, 5.4, 7.1–7.5, 7.7
 */
export function resolveEffectiveSchedule(
  overrides: ScheduleOverride[],
  defaultSchedule: ClassScheduleWithPeriods | null,
  _classId: string,
  date: string,
): OverrideResolutionResult {
  const dayOfWeek = getDayOfWeek(date);

  // Filter to overrides active on this date
  const active = overrides.filter(o => isDateInRange(date, o.start_date, o.end_date));

  // --- Priority 1: holiday ---
  const holidays = sortByCreatedAtDesc(active.filter(o => o.override_type === 'holiday'));
  if (holidays.length > 0) {
    return { schedule: null, override: holidays[0], reason: 'holiday' };
  }

  // --- Priority 2: day-specific half_day_schedule ---
  const daySpecificHalfDay = sortByCreatedAtDesc(
    active.filter(
      o =>
        o.override_type === 'half_day_schedule' &&
        o.day_of_week !== null &&
        o.day_of_week !== 'ALL' &&
        o.day_of_week === dayOfWeek,
    ),
  );
  if (daySpecificHalfDay.length > 0) {
    return {
      schedule: null, // caller must hydrate from alternate_schedule_id
      override: daySpecificHalfDay[0],
      reason: 'half_day_schedule',
    };
  }

  // --- Priority 3: alternate_saturday ---
  if (dayOfWeek === 'SATURDAY') {
    const altSaturdays = sortByCreatedAtDesc(
      active.filter(o => o.override_type === 'alternate_saturday'),
    );
    if (altSaturdays.length > 0) {
      const best = altSaturdays[0];
      const positions = best.working_saturday_positions;
      const position = getSaturdayPositionInMonth(date);
      if (positions && positions.includes(position)) {
        return { schedule: null, override: best, reason: 'alternate_saturday_on' };
      } else {
        return { schedule: null, override: best, reason: 'alternate_saturday_off' };
      }
    }
  }

  // --- Priority 4: ALL-day half_day_schedule ---
  const allDayHalfDay = sortByCreatedAtDesc(
    active.filter(
      o =>
        o.override_type === 'half_day_schedule' &&
        (o.day_of_week === null || o.day_of_week === 'ALL'),
    ),
  );
  if (allDayHalfDay.length > 0) {
    return {
      schedule: null, // caller must hydrate from alternate_schedule_id
      override: allDayHalfDay[0],
      reason: 'all_day_half_day',
    };
  }

  // --- Priority 5: default ---
  return { schedule: defaultSchedule, override: null, reason: 'default' };
}

// ---------------------------------------------------------------------------
// Batch resolver
// ---------------------------------------------------------------------------

/**
 * Resolves effective schedules for every date in [startDate, endDate] inclusive.
 * Returns a Map<YYYY-MM-DD, OverrideResolutionResult>.
 *
 * Requirements: 7.6, 7.8
 */
export function resolveEffectiveScheduleBatch(
  overrides: ScheduleOverride[],
  defaultSchedule: ClassScheduleWithPeriods | null,
  classId: string,
  startDate: string,
  endDate: string,
): Map<string, OverrideResolutionResult> {
  const result = new Map<string, OverrideResolutionResult>();
  const current = new Date(startDate + 'T00:00:00Z');
  const end = new Date(endDate + 'T00:00:00Z');

  while (current <= end) {
    const dateStr = current.toISOString().slice(0, 10);
    result.set(dateStr, resolveEffectiveSchedule(overrides, defaultSchedule, classId, dateStr));
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Conflict validation
// ---------------------------------------------------------------------------

/** Returns true if two date ranges overlap (inclusive on both ends). */
function rangesOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return aStart <= bEnd && bStart <= aEnd;
}

export interface ConflictValidationResult {
  valid: boolean;
  conflictingOverride?: ScheduleOverride;
  message?: string;
}

/**
 * Validates that `incoming` does not conflict with any of the `existing` overrides.
 * On update, pass the override's own `id` inside the request to exclude it from the check.
 * `schoolId` must be provided explicitly since UpdateScheduleOverrideRequest omits it.
 *
 * Requirements: 1.5, 1.6, 3.4, 3.5, 4.4, 6.2
 */
export function validateOverrideConflicts(
  existing: ScheduleOverride[],
  incoming: (CreateScheduleOverrideRequest | (UpdateScheduleOverrideRequest & { id: string })) & { school_id: string },
): ConflictValidationResult {
  const selfId = 'id' in incoming ? incoming.id : undefined;
  const candidates = existing.filter(o => o.id !== selfId && o.school_id === incoming.school_id);

  const inStart = incoming.start_date!;
  const inEnd = incoming.end_date!;
  const inType = incoming.override_type!;

  for (const o of candidates) {
    if (!rangesOverlap(inStart, inEnd, o.start_date, o.end_date)) continue;

    // holiday vs holiday — any overlap is a conflict (Req 1.6)
    if (inType === 'holiday' && o.override_type === 'holiday') {
      return {
        valid: false,
        conflictingOverride: o,
        message: `Overlaps with existing holiday override "${o.name}"`,
      };
    }

    // alternate_saturday — only one active per school per range (Req 3.4, 3.5)
    if (inType === 'alternate_saturday' && o.override_type === 'alternate_saturday') {
      return {
        valid: false,
        conflictingOverride: o,
        message: `Overlaps with existing alternate Saturday override "${o.name}"`,
      };
    }

    // ALL-day half_day_schedule vs ALL-day half_day_schedule (Req 4.4)
    const inIsAll = inType === 'half_day_schedule' && (!incoming.day_of_week || incoming.day_of_week === 'ALL');
    const oIsAll = o.override_type === 'half_day_schedule' && (!o.day_of_week || o.day_of_week === 'ALL');
    if (inIsAll && oIsAll) {
      return {
        valid: false,
        conflictingOverride: o,
        message: `Overlaps with existing all-day half-day schedule override "${o.name}"`,
      };
    }
  }

  return { valid: true };
}
